import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent, DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/auth";
import { memo, useEffect, useState } from "react";
import { ApiChangePasswordReq } from "@shared/authApiTypes";
import { useAccountModal, useCloseAccountModal } from "@/hooks/dialogs";
import { GenericApiOkResp } from "@shared/genericApiTypes";
import { useBackendApi } from "@/hooks/fetch";
import consts from "@shared/consts";
import { txToast } from "./TxToaster";

/**
 * Change Password tab
 */
const ChangePasswordTab = memo(function () {
    const { authData, setAuthData } = useAuth();
    const closeAccountModal = useCloseAccountModal();
    const changePasswordApi = useBackendApi<GenericApiOkResp, ApiChangePasswordReq>({
        method: 'POST',
        path: '/auth/changePassword'
    });

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        if (!authData) return;
        setError('');

        if (newPassword.length < consts.adminPasswordMinLength || newPassword.length > consts.adminPasswordMaxLength) {
            setError(`The password must be between ${consts.adminPasswordMinLength} and ${consts.adminPasswordMaxLength} characters long.`);
            return;
        } else if (newPassword !== newPasswordConfirm) {
            setError('The passwords do not match.');
            return;
        }

        setIsSaving(true);
        changePasswordApi({
            data: {
                newPassword,
                oldPassword: authData.isTempPassword ? undefined : oldPassword,
            },
            error: (error) => {
                setIsSaving(false);
                setError(error);
            },
            success: (data) => {
                setIsSaving(false);
                if ('success' in data) {
                    txToast.success('Password changed successfully!');
                    if (authData.isTempPassword) {
                        setAuthData({
                            ...authData,
                            isTempPassword: false,
                        });
                    }
                    closeAccountModal();
                } else {
                    setError(data.error)
                }
            }
        });
    };

    if (!authData) return null;
    return (
        <div className="pt-2">
            <form onSubmit={handleSubmit} className="space-y-4">
                {authData.isTempPassword && (
                    <div className="rounded-lg border border-[var(--sc-warn)]/20 bg-[var(--sc-warn)]/5 p-3.5 text-xs text-[var(--sc-warn)] leading-relaxed mb-1 shadow-[0_0_15px_rgba(251,191,36,0.03)] animate-pulse">
                        <div className="flex gap-2.5 items-start">
                            <i className="fa-solid fa-triangle-exclamation text-sm mt-0.5" />
                            <div>
                                Your account has a temporary password that must be changed before using the panel.
                                <strong className="block mt-1 font-bold text-white">Make sure to write down your new password.</strong>
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-3.5 pb-2">
                    {!authData.isTempPassword && (
                        <div className="space-y-1">
                            <Label htmlFor="current-password" className="text-[10px] font-bold tracking-wider text-[var(--sc-cyan)]/70 uppercase">
                                Current Password
                            </Label>
                            <Input
                                id="current-password"
                                placeholder="••••••••"
                                type="password"
                                value={oldPassword}
                                autoFocus
                                required
                                className="h-10 bg-[var(--sc-bg-elevated)]/60 border-[var(--sc-border)] text-white font-mono placeholder-[var(--sc-muted)]/40 focus-visible:border-[var(--sc-cyan)]/60 focus-visible:ring-1 focus-visible:ring-[var(--sc-cyan)]/30 focus-visible:shadow-[0_0_12px_rgba(0,255,209,0.12)] transition-all duration-200"
                                onChange={(e) => {
                                    setOldPassword(e.target.value);
                                    setError('');
                                }}
                            />
                        </div>
                    )}
                    <div className="space-y-1">
                        <Label htmlFor="new-password" className="text-[10px] font-bold tracking-wider text-[var(--sc-cyan)]/70 uppercase">
                            New Password
                        </Label>
                        <Input
                            id="new-password"
                            autoComplete="new-password"
                            placeholder="••••••••"
                            type="password"
                            value={newPassword}
                            autoFocus={authData.isTempPassword}
                            required
                            className="h-10 bg-[var(--sc-bg-elevated)]/60 border-[var(--sc-border)] text-white font-mono placeholder-[var(--sc-muted)]/40 focus-visible:border-[var(--sc-cyan)]/60 focus-visible:ring-1 focus-visible:ring-[var(--sc-cyan)]/30 focus-visible:shadow-[0_0_12px_rgba(0,255,209,0.12)] transition-all duration-200"
                            onChange={(e) => {
                                setNewPassword(e.target.value);
                                setError('');
                            }}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="confirm-password" className="text-[10px] font-bold tracking-wider text-[var(--sc-cyan)]/70 uppercase">
                            Confirm Password
                        </Label>
                        <Input
                            id="confirm-password"
                            autoComplete="new-password"
                            placeholder="••••••••"
                            type="password"
                            value={newPasswordConfirm}
                            required
                            className="h-10 bg-[var(--sc-bg-elevated)]/60 border-[var(--sc-border)] text-white font-mono placeholder-[var(--sc-muted)]/40 focus-visible:border-[var(--sc-cyan)]/60 focus-visible:ring-1 focus-visible:ring-[var(--sc-cyan)]/30 focus-visible:shadow-[0_0_12px_rgba(0,255,209,0.12)] transition-all duration-200"
                            onChange={(e) => {
                                setNewPasswordConfirm(e.target.value);
                                setError('');
                            }}
                        />
                    </div>
                </div>

                {error && (
                    <div className="rounded-lg border border-[var(--sc-danger)]/20 bg-[var(--sc-danger)]/5 p-3 text-xs text-destructive-inline text-center leading-relaxed mt-2 animate-bounce">
                        {error}
                    </div>
                )}
                <Button
                    className="w-full h-11 mt-2 bg-[var(--sc-cyan)] text-[var(--sc-bg-base)] border border-transparent hover:bg-[#00e1b9] hover:shadow-[0_0_20px_rgba(0,255,209,0.4)] active:scale-[0.97] transition-all duration-150 font-extrabold uppercase tracking-wider text-sm rounded-lg"
                    type="submit"
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <span className="flex items-center gap-2 justify-center">
                            <i className="fa-solid fa-circle-notch animate-spin text-sm" />
                            Saving...
                        </span>
                    ) : authData.isTempPassword ? (
                        'Update Password'
                    ) : (
                        'Change Password'
                    )}
                </Button>
            </form>
        </div>
    );
});


/**
 * Account Dialog
 */
export default function AccountDialog() {
    const { authData } = useAuth();
    const {
        isAccountModalOpen, setAccountModalOpen,
        setAccountModalTab
    } = useAccountModal();

    useEffect(() => {
        if (!authData) return;
        if (authData.isTempPassword) {
            setAccountModalOpen(true);
            setAccountModalTab('password');
        }
    }, []);

    const dialogSetIsClose = (newState: boolean) => {
        if (!newState && authData && !authData.isTempPassword) {
            setAccountModalOpen(false);
            setTimeout(() => {
                setAccountModalTab('password');
            }, 500);
        }
    }

    if (!authData) return null;
    return (
        <Dialog
            open={isAccountModalOpen}
            onOpenChange={dialogSetIsClose}
        >
            <DialogContent 
                className="bg-[#030509]/95 backdrop-blur-xl border border-[var(--sc-border-mid)] shadow-[var(--sc-glow-md)] sm:max-w-md rounded-xl p-8 gap-0"
                tabIndex={undefined}
            >
                <DialogHeader className="flex flex-col items-center justify-center text-center pb-4">
                    {/* Glowing circular badge with key icon */}
                    <div className="relative mb-3.5 flex h-14 w-14 items-center justify-center rounded-full border border-[var(--sc-cyan)]/30 bg-[var(--sc-teal-deep)]/40 shadow-[0_0_24px_rgba(0,255,209,0.25)]">
                        <i className="fa-solid fa-key text-[var(--sc-cyan)] text-xl animate-pulse" />
                    </div>
                    <DialogTitle className="text-2xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-white via-[var(--sc-text)] to-[var(--sc-cyan)]">
                        {authData.isTempPassword ? 'Welcome to SC Labs' : 'Your Account'}
                    </DialogTitle>
                    {!authData.isTempPassword && (
                        <div className="text-xs font-mono mt-1 px-2.5 py-0.5 rounded bg-[var(--sc-bg-elevated)] border border-[var(--sc-border)] text-[var(--sc-cyan)]/80">
                            {authData.name}
                        </div>
                    )}
                </DialogHeader>
                <ChangePasswordTab />
            </DialogContent>
        </Dialog>
    );
}
